import { translate } from '@teamleader/i18n';
import React, { useCallback, useRef, useState } from 'react';

import DrawSignature from './DrawSignature';
import TextSignature from './TextSignature';
import * as styles from './styles.css';
import Key from '../../../Svg/90x90_key.svg';
import Sign from '../../../Svg/90x90_sign.svg';
import { EVENTS, track } from '../../../Utils/analytics';
import Box from '../Box';
import FormItem from '../FormItem';
import FormLabel from '../FormLabel';
import Link from '../Link';

export enum METHODS {
  DRAW = 'draw',
  TYPE = 'type',
}

export interface Signature {
  method?: METHODS;
  signature: string;
}

interface SignatureInputProps {
  onChange: (signature: Signature) => void;
  preferredMethod?: METHODS;
  width?: number;
  height?: number;
}

const SignatureInput: React.FC<SignatureInputProps> = ({ onChange, preferredMethod = null, width, height }) => {
  const [method, setMethod] = useState<METHODS | null>(preferredMethod);
  const changed = useRef(false);

  const handleSelectDraw = useCallback(() => {
    setMethod(METHODS.DRAW);
    track(EVENTS.CHANGED_SIGNING_STYLE, { style: METHODS.DRAW });
  }, []);

  const handleSelectType = useCallback(() => {
    setMethod(METHODS.TYPE);
    track(EVENTS.CHANGED_SIGNING_STYLE, { style: METHODS.TYPE });
  }, []);

  const handleReset = useCallback(() => {
    const style = method;
    setMethod(preferredMethod);
    onChange({ signature: '' });
    changed.current = false;
    track(EVENTS.RESET_SIGNING_STYLE, { style });
  }, [onChange, method, preferredMethod]);

  const handleChange = useCallback(
    (signature: string) => {
      onChange({ signature, method: method! });

      if (!changed.current) {
        changed.current = true;
        track(EVENTS.STARTED_ADDING_SIGNATURE, { style: method });
      }
    },
    [onChange, method],
  );

  switch (method) {
    case METHODS.TYPE:
      return <TextSignature onChange={handleChange} handleReset={handleReset} width={width} height={height} />;
    case METHODS.DRAW:
      return (
        <DrawSignature
          onChange={handleChange}
          handleReset={handleReset}
          width={width}
          height={height}
          showPlaceholder={preferredMethod === METHODS.DRAW}
          canResetMethod={!preferredMethod}
        />
      );

    default:
      return (
        <FormItem
          label={
            <FormLabel display="flex" justifyContent="space-between">
              {translate('signSidebar.signature')} <Link>{translate('actions.reset')}</Link>
            </FormLabel>
          }
        >
          <Box className={styles['signature']}>
            <Box element="button" type="button" className={styles['signature-option']} onClick={handleSelectDraw}>
              <Sign className={styles['signature-option-icon']} />
              <p>{translate('actions.draw')}</p>
            </Box>
            <Box element="button" type="button" className={styles['signature-option']} onClick={handleSelectType}>
              <Key className={styles['signature-option-icon']} />
              <p>{translate('actions.type')}</p>
            </Box>
          </Box>
        </FormItem>
      );
  }
};

export default SignatureInput;
